import { MouseEvent, useMemo } from 'react';
import { fetchWithRequestId, withRequestId } from '../../utils';
import { useAuthenticatedFetch } from '../use-authenticated-fetch/use-authenticated-fetch';

interface DownloadOptions {
  /**
   * The filename for the downloaded file.
   */
  filename?: string;

  /**
   * The mime type of the file. Only affects blob downloads. Defaults to 'application/octet-stream'.
   */
  mimeType?: string;

  /**
   * Adds the current authorization header to the request. Only affects link downloads. Defaults to 'true'.
   */
  addAuthorizationHeader?: boolean;
}

type DownloadBlobFunction = (data: BlobPart, options?: DownloadOptions) => void;

type DownloadLinkFunction = (url: string, options?: DownloadOptions) => Promise<void>;

type AnchorClickHandlerFunction = (
  options?: DownloadOptions,
) => (event: MouseEvent<HTMLAnchorElement>) => Promise<void>;

interface UseDownloadResult {
  downloadBlob: DownloadBlobFunction;
  downloadLink: DownloadLinkFunction;
  clickHandler: AnchorClickHandlerFunction;
}

export function useDownload(options?: DownloadOptions): UseDownloadResult {
  const authenticatedFetch = useAuthenticatedFetch();

  return useMemo(() => {
    const download = (blob: Blob, opts?: DownloadOptions): void => {
      const anchor = document.createElement('a');
      anchor.download = opts?.filename ?? options?.filename ?? '';
      anchor.href = URL.createObjectURL(blob);
      anchor.click();
    };

    const downloadLink: DownloadLinkFunction = async (url, opts) => {
      const fetchImplementation = fetchWithRequestId(
        opts?.addAuthorizationHeader ?? options?.addAuthorizationHeader ?? true ? authenticatedFetch : fetch,
      );
      const response = await fetchImplementation(url);

      if (!response.ok) {
        throw withRequestId(new Error('Failed to download file.'), response.requestId);
      }

      return download(await response.blob(), opts);
    };

    return {
      downloadBlob: (data, opts) =>
        download(new Blob([data], { type: opts?.mimeType ?? options?.mimeType ?? 'application/octet-stream' }), opts),
      downloadLink,
      clickHandler: (opts) => (event) => {
        event.preventDefault();
        return downloadLink(event.currentTarget.href, {
          ...opts,
          filename: opts?.filename || event.currentTarget.download || options?.filename,
        });
      },
    };
  }, [options?.filename, options?.mimeType, options?.addAuthorizationHeader, authenticatedFetch]);
}
